/**
 * Cluster — marker clustering using supercluster.
 * Groups nearby pins into numbered bubbles at low zoom.
 * Clicking a cluster zooms to expand it.
 * Individual pins retain full popup and a11y behavior.
 */

import Supercluster from 'supercluster';
import { getOpenStatus, getAcceptingStatus } from './markers.js';

/**
 * Create a cluster marker DOM element (circle with count).
 */
function createClusterElement(count, brandColor) {
  const el = document.createElement('div');
  el.className = 'pm-cluster-marker';

  // Size scales with count
  let size, fontSize;
  if (count < 10) {
    size = 36;
    fontSize = 13;
  } else if (count < 50) {
    size = 42;
    fontSize = 14;
  } else if (count < 100) {
    size = 48;
    fontSize = 15;
  } else {
    size = 54;
    fontSize = 16;
  }

  el.style.cssText = `
    width: ${size}px;
    height: ${size}px;
    border-radius: 50%;
    background: ${brandColor};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: ${fontSize}px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    border: 3px solid rgba(255,255,255,0.8);
    transition: transform 0.15s ease;
    line-height: 1;
  `;

  el.textContent = count;

  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.setAttribute('aria-label', `Cluster of ${count} locations. Click to zoom in.`);

  // Hover effect

  return el;
}

/**
 * Create an individual marker DOM element (same as markers.js).
 */
function createMarkerElement(brandColor) {
  const el = document.createElement('div');
  el.className = 'pm-marker-icon';
  el.innerHTML = `<svg width="28" height="38" viewBox="0 0 28 38" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 24 14 24s14-13.5 14-24C28 6.268 21.732 0 14 0z" fill="${brandColor}"/>
    <circle cx="14" cy="14" r="6" fill="white"/>
  </svg>`;
  el.style.cursor = 'pointer';
  el.style.width = '28px';
  el.style.height = '38px';
  return el;
}


export class ClusterManager {
  /**
   * @param {maplibregl.Map} map
   * @param {Array} locations - all location objects
   * @param {Object} config - widget config
   * @param {Function} buildPopupHTML - imported from markers.js
   */
  constructor(map, locations, config, buildPopupHTML) {
    this.map = map;
    this.allLocations = locations;
    this.config = config;
    this.buildPopupHTML = buildPopupHTML;
    this.maplibregl = window.maplibregl;

    // Currently rendered markers/clusters on map
    this.rendered = new Map(); // key -> { marker, type }

    // Active popup tracking
    this.activePopup = null;
    this.activePopupLngLat = null;

    // Visible locations (for filter integration)
    this.visibleLocations = locations;
    this.visibleIndices = new Set(locations.map((_, i) => i));

    // Compact mode
    this.compact = map.getContainer().offsetHeight < 500;

    // Initialize supercluster
    this.index = null;
    this._rebuildIndex();

    // Bind render to map events
    this._onRender = this._render.bind(this);
    this._moveRaf = null;
    this._onMove = () => {
      if (!this._moveRaf) {
        this._moveRaf = requestAnimationFrame(() => {
          this._render();
          this._moveRaf = null;
        });
      }
    };
    map.on('move', this._onMove);
    map.on('moveend', this._onRender);
    map.on('zoomend', this._onRender);

    // Re-center on active popup after zoom
    map.on('zoomend', () => {
      if (this.activePopup && this.activePopup.isOpen() && this.activePopupLngLat) {
        setTimeout(() => {
          const markerPoint = map.project(this.activePopupLngLat);
          const container = map.getContainer();
          const w = container.offsetWidth;
          const h = container.offsetHeight;
          // Place marker at 70% down so popup has room above
          const targetX = w / 2;
          const targetY = h * 0.7;
          const dx = markerPoint.x - targetX;
          const dy = markerPoint.y - targetY;
          if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
            map.panBy([dx, dy], { animate: true, duration: 300 });
          }
        }, 100);
      }
    });

    // Initial render after style loads
    if (map.isStyleLoaded()) {
      this._render();
    } else {
      map.once('load', () => this._render());
    }
  }

  /**
   * Rebuild the supercluster index (called on init and filter changes).
   */
  _rebuildIndex() {
    const points = [];
    this.visibleLocations.forEach((loc, i) => {
      points.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [loc.lng, loc.lat],
        },
        properties: {
          index: this.allLocations.indexOf(loc),
          locationIndex: i,
        },
      });
    });

    this.index = new Supercluster({
      radius: 60,
      maxZoom: 16,
      minPoints: 2,
    });
    this.index.load(points);
  }

  /**
   * Main render loop — called on every map move/zoom.
   * Compares what should be visible vs what's currently rendered,
   * and only adds/removes what changed.
   */
  _render() {
    const map = this.map;
    const bounds = map.getBounds();
    const zoom = Math.floor(map.getZoom());

    const bbox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ];

    const clusters = this.index.getClusters(bbox, zoom);

    // Build set of keys for what should be visible
    const nextKeys = new Set();

    clusters.forEach(feature => {
      const coords = feature.geometry.coordinates;
      const props = feature.properties;

      if (props.cluster) {
        const key = `cluster-${props.cluster_id}`;
        nextKeys.add(key);

        if (!this.rendered.has(key)) {
          this._addCluster(key, coords, props);
        }
      } else {
        const key = `point-${props.index}`;
        nextKeys.add(key);

        if (!this.rendered.has(key)) {
          this._addPoint(key, coords, props.index);
        }
      }
    });

    // Remove markers no longer visible
    for (const [key, entry] of this.rendered) {
      if (!nextKeys.has(key)) {
        entry.marker.remove();
        if (entry.popup && entry.popup.isOpen()) {
          entry.popup.remove();
        }
        this.rendered.delete(key);
      }
    }
  }

  /**
   * Add a cluster bubble to the map.
   */
  _addCluster(key, coords, props) {
    const el = createClusterElement(props.point_count, this.config.brandColor);
    const map = this.map;
    const index = this.index;

    const marker = new this.maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat(coords)
      .addTo(map);

    // Click → zoom to expansion zoom
    const handleClick = () => {
      // Close any open popup
      if (this.activePopup && this.activePopup.isOpen()) {
        this.activePopup.remove();
        this.activePopup = null;
      }

      const expansionZoom = Math.min(index.getClusterExpansionZoom(props.cluster_id), 18);
      map.flyTo({
        center: coords,
        zoom: expansionZoom,
        duration: 500,
      });
    };

    el.addEventListener('click', handleClick);
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    });

    this.rendered.set(key, { marker, type: 'cluster' });
  }

  /**
   * Add an individual point marker to the map.
   */
  _addPoint(key, coords, locationIndex) {
    const loc = this.allLocations[locationIndex];
    const el = createMarkerElement(this.config.brandColor);

    // A11y
    el.setAttribute('tabindex', '0');
    el.setAttribute('role', 'button');
    const labelParts = [loc.name];
    const status = getOpenStatus(loc);
    if (status) labelParts.push(status === 'open' ? 'Open now' : 'Closed');
    const accepting = getAcceptingStatus(loc);
    if (accepting) labelParts.push(accepting.text);
    if (loc.specialty) labelParts.push(loc.specialty);
    labelParts.push('Press Enter for details.');
    el.setAttribute('aria-label', labelParts.join('. '));

    const marker = new this.maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat(coords)
      .addTo(this.map);

    // Popup
    const popupHtml = this.buildPopupHTML(loc, this.config, this.compact);
    const popup = new this.maplibregl.Popup({
      maxWidth: window.innerWidth <= 640 ? '220px' : (this.compact ? '400px' : '300px'),
      closeButton: true,
      closeOnClick: false,
      anchor: 'bottom',
      offset: [0, -38],
      className: 'pm-maplibre-popup',
    }).setHTML(popupHtml);

    marker.setPopup(popup);

    popup.on('open', () => {
      // Close previous popup
      if (this.activePopup && this.activePopup !== popup && this.activePopup.isOpen()) {
        this.activePopup.remove();
      }
      this.activePopup = popup;
      this.activePopupLngLat = coords;

      // Auto-pan
      setTimeout(() => {
        const popupEl = popup.getElement();
        if (!popupEl) return;
        const container = this.map.getContainer();
        const mapRect = container.getBoundingClientRect();
        const popupRect = popupEl.getBoundingClientRect();

        let needsPan = false;
        let dx = 0, dy = 0;
        const pad = 40;

        if (popupRect.top < mapRect.top + pad) {
          dy = popupRect.top - mapRect.top - pad;
          needsPan = true;
        }
        if (popupRect.bottom > mapRect.bottom - pad) {
          dy = popupRect.bottom - mapRect.bottom + pad;
          needsPan = true;
        }
        if (popupRect.left < mapRect.left + pad) {
          dx = popupRect.left - mapRect.left - pad;
          needsPan = true;
        }
        if (popupRect.right > mapRect.right - pad) {
          dx = popupRect.right - mapRect.right + pad;
          needsPan = true;
        }
        if (needsPan) {
          this.map.panBy([dx, dy], { animate: true, duration: 300 });
        }
      }, 50);

      // A11y enhancements
      const popupEl = popup.getElement();
      if (popupEl) {
        const container = popupEl.querySelector('.pm-popup');
        if (container) {
          container.setAttribute('role', 'dialog');
          container.setAttribute('aria-label', `Details for ${loc.name}`);
          const firstFocusable = container.querySelector('a, button, [tabindex]');
          if (firstFocusable) setTimeout(() => firstFocusable.focus(), 50);
        }
        const closeBtn = popupEl.querySelector('.maplibregl-popup-close-button');
        if (closeBtn) {
          closeBtn.setAttribute('aria-label', `Close details for ${loc.name}`);
          closeBtn.setAttribute('role', 'button');
        }
      }
    });

    popup.on('close', () => {
      if (this.activePopup === popup) {
        this.activePopup = null;
        this.activePopupLngLat = null;
      }
    });

    // Keyboard support
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        marker.togglePopup();
      }
    });

    this.rendered.set(key, { marker, popup, type: 'point', locationIndex });
  }

  /**
   * Update visible locations (called by filters).
   * Rebuilds the cluster index and re-renders.
   */
  updateLocations(filteredLocations) {
    // Close any open popup
    if (this.activePopup && this.activePopup.isOpen()) {
      this.activePopup.remove();
      this.activePopup = null;
      this.activePopupLngLat = null;
    }

    this.visibleLocations = filteredLocations;
    this._rebuildIndex();

    // Clear all current markers
    for (const [key, entry] of this.rendered) {
      entry.marker.remove();
      if (entry.popup && entry.popup.isOpen()) entry.popup.remove();
    }
    this.rendered.clear();

    // Re-render
    this._render();
  }

  /**
   * Open a specific location's popup (called by sidebar click).
   * Zooms in enough to de-cluster, then opens the popup.
   */
  openLocation(locationIndex) {
    const loc = this.allLocations[locationIndex];
    if (!loc) return;

    // Close any open popup
    if (this.activePopup && this.activePopup.isOpen()) {
      this.activePopup.remove();
    }

    const targetZoom = Math.max(this.map.getZoom(), 16);

    this.map.once('moveend', () => {
      // After zoom, find the rendered point and open its popup
      setTimeout(() => {
        const key = `point-${locationIndex}`;
        const entry = this.rendered.get(key);
        if (entry && entry.popup) {
          entry.marker.togglePopup();
        }
      }, 100);
    });

    this.map.flyTo({
      center: [loc.lng, loc.lat],
      zoom: targetZoom,
      duration: 800,
    });
  }

  /**
   * Get all rendered individual markers (for compatibility with sidebar).
   * Returns array of { marker, popup, element, location, index } like addMarkers().
   */
  getMarkerRefs() {
    return this.allLocations.map((loc, index) => ({
      marker: null,  // Dynamic — may not be rendered yet
      popup: null,
      element: null,
      location: loc,
      index,
    }));
  }

  /**
   * Clean up event listeners.
   */
  destroy() {
    this.map.off('move', this._onMove);
    this.map.off('moveend', this._onRender);
    this.map.off('zoomend', this._onRender);
    if (this._moveRaf) cancelAnimationFrame(this._moveRaf);
    for (const [, entry] of this.rendered) {
      entry.marker.remove();
    }
    this.rendered.clear();
  }
}
